'use client';

import type {
  ReservationPerson,
  ReservationService,
  ReservationSlot,
} from '@eliadmin/core/src/api/reservation/reservation-api';
import {
  fetchReservationSlots,
  ReservationSlotType,
} from '@eliadmin/core/src/api/reservation/reservation-api';
import { ReservationCalendarHeader } from './reservation-calendar-header';
import { ReservationCalendarSlots } from './reservation-calendar-slots';
import { specificDatesConfiguration } from './specific-dates';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { addWeeks, endOfWeek, format, isSameDay, startOfWeek } from 'date-fns';
import type { ReservationFormSchemaType } from '@eliadmin/core/src/components/reservation/reservation-form';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';

type ReservationCalendarProps = {
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  selectedService?: ReservationService;
  selectedPerson?: ReservationPerson;
};

const MAX_AUTO_ADVANCE_WEEKS = 12;

const hasFreeSlot = (slots?: ReservationSlot[][]) => {
  return slots?.some(day => day.some(slot => slot.type === ReservationSlotType.FREE)) ?? false;
};

const getFirstFreeSlotDate = (slots?: ReservationSlot[][]) => {
  const firstFreeSlot = slots
    ?.flatMap(day => day)
    .find(slot => slot.type === ReservationSlotType.FREE);

  return firstFreeSlot ? new Date(firstFreeSlot.datetime_start) : undefined;
};

export const ReservationCalendar = ({
  weekStartsOn = 1,
  selectedService,
  selectedPerson,
}: ReservationCalendarProps) => {
  const locale = useLocaleStore(s => s.currentLocale);
  const { t } = useTranslations();

  const templateSettings = useGlobalStore(s => s.templateSettings);

  const [dateRange, setDateRange] = useState({
    dateFrom: startOfWeek(new Date(), { weekStartsOn }),
    dateTo: endOfWeek(new Date(), { weekStartsOn }),
  });
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(() => dateRange.dateFrom);

  const { dateFrom, dateTo } = dateRange;

  const form = useFormContext<ReservationFormSchemaType>();
  const reservationPersonId = form.watch('reservation_person');
  const reservationServiceId = form.watch('reservation_service');
  const reservedAt = form.watch('reserved_at');
  const lastAutoSearchRangeRef = useRef<string | null>(null);
  const lastAppliedReservedAtRef = useRef<string | null>(null);
  const [isSearchingNextAvailableDate, setIsSearchingNextAvailableDate] = useState(false);
  const [unavailableRequestedSlot, setUnavailableRequestedSlot] = useState<{
    reservedAt: string;
    personName: string;
  } | null>(null);

  const handleDateChange = (newDateFrom: Date, newDateTo: Date, selectedDate?: Date) => {
    form.setValue('reserved_at', '', { shouldDirty: true, shouldValidate: false });
    setUnavailableRequestedSlot(null);
    setSelectedCalendarDate(selectedDate ?? newDateFrom);
    setDateRange({ dateFrom: newDateFrom, dateTo: newDateTo });
  };

  const fetchReservationSlotsData = async () => {
    return await fetchReservationSlots({
      locale,
      body: {
        date_from: dateFrom.toDateString(),
        date_to: dateTo.toDateString(),
        free_slots: true,
        reserved_slots: true,
        break_slots: true,
        nonreservable_slots: true,
        reservation_person: { id: parseInt(reservationPersonId) },
        reservation_service: { id: parseInt(reservationServiceId) },
      },
    });
  };

  const { data: slots, isLoading } = useQuery({
    queryKey: [
      reservationPersonId,
      reservationServiceId,
      dateFrom.toISOString(),
      dateTo.toISOString(),
    ],
    queryFn: fetchReservationSlotsData,
    enabled: !!dateFrom && !!dateTo && !!reservationPersonId && !!reservationServiceId,
  });

  useEffect(() => {
    if (!reservedAt || lastAppliedReservedAtRef.current === reservedAt) return;

    const reservedAtDate = new Date(reservedAt);
    if (Number.isNaN(reservedAtDate.getTime())) return;

    lastAppliedReservedAtRef.current = reservedAt;
    setSelectedCalendarDate(reservedAtDate);
    setDateRange({
      dateFrom: startOfWeek(reservedAtDate, { weekStartsOn }),
      dateTo: endOfWeek(reservedAtDate, { weekStartsOn }),
    });
  }, [reservedAt, weekStartsOn]);

  useEffect(() => {
    if (!slots || isLoading || hasFreeSlot(slots)) return;
    if (!reservationPersonId || !reservationServiceId) return;

    const searchKey = [
      reservationPersonId,
      reservationServiceId,
      dateFrom.toISOString(),
      dateTo.toISOString(),
    ].join(':');

    if (lastAutoSearchRangeRef.current === searchKey) return;
    lastAutoSearchRangeRef.current = searchKey;

    let isCancelled = false;

    const findNextAvailableWeek = async () => {
      setIsSearchingNextAvailableDate(true);

      try {
        for (let offset = 1; offset <= MAX_AUTO_ADVANCE_WEEKS; offset += 1) {
          const nextDateFrom = startOfWeek(addWeeks(dateFrom, offset), { weekStartsOn });
          const nextDateTo = endOfWeek(nextDateFrom, { weekStartsOn });
          const nextSlots = await fetchReservationSlots({
            locale,
            body: {
              date_from: nextDateFrom.toDateString(),
              date_to: nextDateTo.toDateString(),
              free_slots: true,
              reserved_slots: true,
              break_slots: true,
              nonreservable_slots: true,
              reservation_person: { id: parseInt(reservationPersonId) },
              reservation_service: { id: parseInt(reservationServiceId) },
            },
          });

          if (isCancelled) return;

          if (hasFreeSlot(nextSlots)) {
            const firstFreeSlotDate = getFirstFreeSlotDate(nextSlots);
            form.setValue('reserved_at', '', { shouldDirty: true, shouldValidate: false });
            setSelectedCalendarDate(firstFreeSlotDate ?? nextDateFrom);
            setIsSearchingNextAvailableDate(false);
            setDateRange({ dateFrom: nextDateFrom, dateTo: nextDateTo });
            return;
          }
        }
      } finally {
        if (!isCancelled) {
          setIsSearchingNextAvailableDate(false);
        }
      }
    };

    void findNextAvailableWeek();

    return () => {
      isCancelled = true;
    };
  }, [
    dateFrom,
    dateTo,
    form,
    isLoading,
    locale,
    reservationPersonId,
    reservationServiceId,
    slots,
    weekStartsOn,
  ]);

  useEffect(() => {
    if (!slots || isLoading || isSearchingNextAvailableDate) return;

    if (reservedAt) {
      const reservedAtDate = new Date(reservedAt);
      const reservedSlotExists = slots
        .flatMap(day => day)
        .some(
          slot =>
            slot.type === ReservationSlotType.FREE &&
            new Date(slot.datetime_start).getTime() === reservedAtDate.getTime(),
        );

      if (reservedSlotExists) {
        setUnavailableRequestedSlot(null);

        if (!isSameDay(reservedAtDate, selectedCalendarDate)) {
          setSelectedCalendarDate(reservedAtDate);
        }

        return;
      }

      setUnavailableRequestedSlot({
        reservedAt,
        personName: selectedPerson?.name ?? '',
      });
      form.setValue('reserved_at', '', { shouldDirty: true, shouldValidate: false });
    }

    const firstFreeSlotDate = getFirstFreeSlotDate(slots);

    if (firstFreeSlotDate && !isSameDay(firstFreeSlotDate, selectedCalendarDate)) {
      setSelectedCalendarDate(firstFreeSlotDate);
    }
  }, [
    form,
    isLoading,
    isSearchingNextAvailableDate,
    reservedAt,
    selectedCalendarDate,
    selectedPerson?.name,
    slots,
  ]);

  const [hasMounted, setHasMounted] = useState(false); // State to track component mount
  const isDesktop = useBreakpoint('sm'); // Use your media query hook

  useEffect(() => {
    setHasMounted(true); // Set true after component mounts
  }, []);

  const direction = hasMounted ? (isDesktop ? 'column' : 'row') : 'column';

  return (
    <div className="flex w-full flex-col items-center justify-center gap-8  sm:gap-6">
      {unavailableRequestedSlot && (
        <p
          className={cn(
            'w-full rounded-[8px] bg-destructive px-4 py-3 text-center text-sm font-semibold text-destructive-foreground sm:text-base',
            'ea-reservation-calendar__unavailable-requested-slot',
          )}
        >
          {`Vybraný termín (${format(new Date(unavailableRequestedSlot.reservedAt), 'dd.MM.yyyy HH:mm')}) nie je pre osobu "${unavailableRequestedSlot.personName}" dostupný.`}
        </p>
      )}
      <ReservationCalendarHeader
        dateFrom={dateFrom}
        dateTo={dateTo}
        selectedDate={selectedCalendarDate}
        onDateChange={handleDateChange}
      />
      <ReservationCalendarSlots
        slots={slots}
        selectedService={selectedService}
        isSlotsLoading={isLoading || isSearchingNextAvailableDate}
        dateFrom={dateFrom}
        locale={locale}
        specificDatesConfiguration={specificDatesConfiguration}
        direction={
          templateSettings?.reservation?.force_row_calendar_on_desktop?.enabled ? 'row' : direction
        }
      />
      <div>
        {form.formState.errors.reserved_at && (
          <p
            className={cn(
              'flex h-16 items-center justify-center rounded-sm bg-destructive text-lg font-semibold text-destructive-foreground sm:h-24',
              'ea-reservation-calendar__error-wrapper',
            )}
          >
            {form.formState.errors.reserved_at.message}
          </p>
        )}
      </div>
      {!slots && !isLoading && (
        <p
          className={cn(
            'flex h-16 items-center justify-center rounded-sm bg-destructive text-lg font-semibold text-destructive-foreground sm:h-24',
            'ea-reservation-calendar__error-wrapper',
          )}
        >
          {t('text1320')}
        </p>
      )}
    </div>
  );
};
